export type PlacementHelpAxis = 'X' | 'Y';

export type PlacementHelpTick = {
	axis: PlacementHelpAxis;
	tickIndex?: number | null;
};

export type PlacementHelpCell = {
	row: number;
	column: number;
};

export type TemplatePlacementHelpQuestion = {
	id: string;
	axis: string;
	question: { languageID: string; text: string }[];
	answers: {
		id: string;
		label: { languageID: string; text: string }[];
		tickIndex?: number | null;
	}[];
};

export function questionsOnTemplate<T = TemplatePlacementHelpQuestion>(template: unknown): T[] {
	if (!template || typeof template !== 'object' || !('placementHelpQuestions' in template)) {
		return [];
	}

	return (template as { placementHelpQuestions?: T[] | null }).placementHelpQuestions ?? [];
}

export function asPlacementHelpAxis(axis: string): PlacementHelpAxis {
	return axis === 'Y' ? 'Y' : 'X';
}

export function textInLanguage(
	value: { languageID: string; text: string }[] | undefined,
	languageID: string,
) {
	return value?.find((item) => item.languageID === languageID)?.text?.trim() ?? '';
}

export function placementHelpQuestionsAreReady(
	questions: TemplatePlacementHelpQuestion[],
	languageID: string,
) {
	return questions.every((question) => {
		if (!textInLanguage(question.question, languageID)) {
			return false;
		}

		return !question.answers.some(
			(answer) => typeof answer.tickIndex === 'number' && !textInLanguage(answer.label, languageID),
		);
	});
}

export const withPlacementHelpName = (text: string, name: string) =>
	text.replaceAll('{name}', name);

export function placementHelpPositionLabel(args: {
	cellLabel?: string | null;
	xTick?: string | null;
	yTick?: string | null;
}) {
	const label = args.cellLabel?.trim();
	if (label) {
		return label;
	}

	return [args.xTick, args.yTick]
		.flatMap((tick) => {
			const text = tick?.trim();
			return text ? [text] : [];
		})
		.join(' · ');
}

export function middleTick(count: number) {
	if (count <= 1) {
		return 0;
	}

	return Math.round((count - 1) / 2);
}

function clampTick(value: number, count: number) {
	if (count <= 0) {
		return 0;
	}

	return Math.min(count - 1, Math.max(0, value));
}

function averageTick(values: number[], count: number, fallback: number) {
	if (values.length === 0) {
		return clampTick(fallback, count);
	}

	return clampTick(
		Math.round(values.reduce((total, value) => total + value, 0) / values.length),
		count,
	);
}

export function averagePlacementHelpCell(args: {
	ticks: PlacementHelpTick[];
	rowCount: number;
	columnCount: number;
	current?: PlacementHelpCell;
}): PlacementHelpCell {
	const columns = args.ticks.flatMap((tick) =>
		tick.axis === 'X' && typeof tick.tickIndex === 'number' ? [tick.tickIndex] : [],
	);
	const rows = args.ticks.flatMap((tick) =>
		tick.axis === 'Y' && typeof tick.tickIndex === 'number' ? [tick.tickIndex] : [],
	);

	return {
		column: averageTick(
			columns,
			args.columnCount,
			args.current?.column ?? middleTick(args.columnCount),
		),
		row: averageTick(rows, args.rowCount, args.current?.row ?? middleTick(args.rowCount)),
	};
}
